/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * Failure class the hook sender observed. http means the control plane answered with a retryable status.
 */
export const Cause = {
  Dns: "dns",
  Tls: "tls",
  Timeout: "timeout",
  Canceled: "canceled",
  Connection: "connection",
  UnreadableResponse: "unreadable-response",
  Unknown: "unknown",
  Http: "http",
} as const;
/**
 * Failure class the hook sender observed. http means the control plane answered with a retryable status.
 */
export type Cause = ClosedEnum<typeof Cause>;

/**
 * Whether a failed send was a live hook or a spool-drain replay.
 */
export const DeliveryPhase = {
  Live: "live",
  Drain: "drain",
} as const;
/**
 * Whether a failed send was a live hook or a spool-drain replay.
 */
export type DeliveryPhase = ClosedEnum<typeof DeliveryPhase>;

/**
 * Canonical type of the event whose delivery failed. Bounded to the hook activity event vocabulary because it is a metric dimension; a report never describes another report.
 */
export const FailedEventType = {
  SessionStarted: "session.started",
  SessionUpdated: "session.updated",
  SessionEnded: "session.ended",
  McpInventory: "mcp.inventory",
  PromptSubmitted: "prompt.submitted",
  ToolRequested: "tool.requested",
  ToolCompleted: "tool.completed",
  ToolFailed: "tool.failed",
  AssistantResponded: "assistant.responded",
  AssistantThought: "assistant.thought",
  UsageReported: "usage.reported",
  SkillActivated: "skill.activated",
  NotificationReported: "notification.reported",
} as const;
/**
 * Canonical type of the event whose delivery failed. Bounded to the hook activity event vocabulary because it is a metric dimension; a report never describes another report.
 */
export type FailedEventType = ClosedEnum<typeof FailedEventType>;

/**
 * Device-side delivery failure report, sent with event type client.delivery_failed and a required Idempotency-Key so each failure counts once. The speakeasy-hooks binary writes one when an event could not reach the control plane and redelivers it from the offline spool once connectivity returns, so the record describes the original attempt rather than the request that carried it. It is diagnostic only and never counts as agent activity.
 */
export type HookDeliveryFailureData = {
  /**
   * CPU architecture of the device.
   */
  arch?: string | undefined;
  /**
   * RFC3339 time the failed send started, on the device clock.
   */
  attemptedAt: Date;
  /**
   * Number of send attempts the client made.
   */
  attempts?: number | undefined;
  /**
   * speakeasy-hooks binary version that made the attempt.
   */
  binaryVersion?: string | undefined;
  /**
   * Failure class the hook sender observed. http means the control plane answered with a retryable status.
   */
  cause: Cause;
  /**
   * Whether a failed send was a live hook or a spool-drain replay.
   */
  deliveryPhase: DeliveryPhase;
  /**
   * Wall time the client spent on the send across its attempts, in milliseconds.
   */
  elapsedMs?: number | undefined;
  /**
   * Canonical type of the event whose delivery failed. Bounded to the hook activity event vocabulary because it is a metric dimension; a report never describes another report.
   */
  failedEventType: FailedEventType;
  /**
   * Idempotency key of the event that failed, so the report joins to the row its eventual replay produces.
   */
  failedIdempotencyKey?: string | undefined;
  /**
   * Whether the send gated an agent action (a verdict was awaited) rather than observing one.
   */
  gating?: boolean | undefined;
  /**
   * Coding agent harness the hook ran under.
   */
  harness?: string | undefined;
  /**
   * Harness variant, when the harness distinguishes one.
   */
  harnessVariant?: string | undefined;
  /**
   * Harness version, when known.
   */
  harnessVersion?: string | undefined;
  /**
   * Operating system of the device.
   */
  os?: string | undefined;
  /**
   * Session id of the event that failed.
   */
  sessionId?: string | undefined;
  /**
   * Events waiting in the device's offline spool when the report was written.
   */
  spoolDepth?: number | undefined;
  /**
   * HTTP status of the final attempt, 0 when no response arrived.
   */
  statusCode?: number | undefined;
};

/** @internal */
export const Cause$outboundSchema: z.ZodMiniEnum<typeof Cause> = z.enum(Cause);

/** @internal */
export const DeliveryPhase$outboundSchema: z.ZodMiniEnum<typeof DeliveryPhase> =
  z.enum(DeliveryPhase);

/** @internal */
export const FailedEventType$outboundSchema: z.ZodMiniEnum<
  typeof FailedEventType
> = z.enum(FailedEventType);

/** @internal */
export type HookDeliveryFailureData$Outbound = {
  arch?: string | undefined;
  attempted_at: string;
  attempts?: number | undefined;
  binary_version?: string | undefined;
  cause: string;
  delivery_phase: string;
  elapsed_ms?: number | undefined;
  failed_event_type: string;
  failed_idempotency_key?: string | undefined;
  gating?: boolean | undefined;
  harness?: string | undefined;
  harness_variant?: string | undefined;
  harness_version?: string | undefined;
  os?: string | undefined;
  session_id?: string | undefined;
  spool_depth?: number | undefined;
  status_code?: number | undefined;
};

/** @internal */
export const HookDeliveryFailureData$outboundSchema: z.ZodMiniType<
  HookDeliveryFailureData$Outbound,
  HookDeliveryFailureData
> = z.pipe(
  z.object({
    arch: z.optional(z.string()),
    attemptedAt: z.pipe(z.date(), z.transform(v => v.toISOString())),
    attempts: z.optional(z.int()),
    binaryVersion: z.optional(z.string()),
    cause: Cause$outboundSchema,
    deliveryPhase: DeliveryPhase$outboundSchema,
    elapsedMs: z.optional(z.int()),
    failedEventType: FailedEventType$outboundSchema,
    failedIdempotencyKey: z.optional(z.string()),
    gating: z.optional(z.boolean()),
    harness: z.optional(z.string()),
    harnessVariant: z.optional(z.string()),
    harnessVersion: z.optional(z.string()),
    os: z.optional(z.string()),
    sessionId: z.optional(z.string()),
    spoolDepth: z.optional(z.int()),
    statusCode: z.optional(z.int()),
  }),
  z.transform((v) => {
    return remap$(v, {
      attemptedAt: "attempted_at",
      binaryVersion: "binary_version",
      deliveryPhase: "delivery_phase",
      elapsedMs: "elapsed_ms",
      failedEventType: "failed_event_type",
      failedIdempotencyKey: "failed_idempotency_key",
      harnessVariant: "harness_variant",
      harnessVersion: "harness_version",
      sessionId: "session_id",
      spoolDepth: "spool_depth",
      statusCode: "status_code",
    });
  }),
);

export function hookDeliveryFailureDataToJSON(
  hookDeliveryFailureData: HookDeliveryFailureData,
): string {
  return JSON.stringify(
    HookDeliveryFailureData$outboundSchema.parse(hookDeliveryFailureData),
  );
}
